import React, { useContext, useMemo } from 'react';
import { FormControl, Select, MenuItem, CircularProgress, Box } from '@mui/material';
import { useGridApiContext } from '@mui/x-data-grid';
import getUsersName from '../../../helpers/users/utils';
import { useFetchUsers } from '../../../data/generic-queries/users';
import configs from './configs';
import { NotificationContext } from '../../../NotificationContext';

const EditUserCell = ({
    id,
    field,
    row,
    query,
    secondaryLabelField,
    table,
    includeUnassigned = true,
    sortFn,
    highlightUser = null,
    highlightIcon = null,
    // wrap
}) => {
    const apiRef = useGridApiContext();
    const { showSnackbar } = useContext(NotificationContext);
    const { data: rawData, isLoading } = useFetchUsers({ ...query, sort: { name: 1 }, project: { name: 1, license_number: 1 } });
    const data = useMemo(() => {
        if (!rawData) return [];

        const sorted = sortFn ? rawData.toSorted(sortFn) : [...rawData];

        if (highlightUser) {
            const i = sorted.findIndex((u) => u._id === highlightUser);
            if (i >= 0) sorted.unshift(sorted.splice(i, 1)[0]);
        }

        return sorted;
    }, [rawData, sortFn, highlightUser]);

    const mutateConfig = configs[table];
    const { mutateAsync } = mutateConfig.useHook();

    const handleMutate = async (event) => {
        const { value } = event.target;

        const extractIdFields = mutateConfig.schema?.meta()?.extractIdFields || [];
        const formattedRow = {
            ...row,
            [field]: value,
        };

        extractIdFields.forEach((_field) => {
            if (formattedRow[_field] && formattedRow[_field]._id) {
                formattedRow[_field] = formattedRow[_field]._id;
            }
        });

        try {
            await mutateConfig.schema.validateAt(field, formattedRow);
        } catch (e) {
            showSnackbar({ severity: 'error', message: e.message });
            return;
        }

        apiRef.current.setEditCellValue({
            id,
            field,
            value,
        });

        const body = {
            [field]: value ?? null,
        };
        mutateConfig.handleMutate({ id, body }, { mutate: mutateAsync });
        return apiRef.current.setCellMode(id, field, 'view');
    };

    if (isLoading) {
        return <CircularProgress />;
    }

    return (
        <FormControl
            fullWidth
            variant="outlined"
            sx={{
                '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                        border: 'none',
                    },
                },
            }}
        >
            <Select autoFocus fullWidth value={row[field]?._id ?? ''} onChange={handleMutate}>
                {includeUnassigned && <MenuItem value={undefined}>Unassigned</MenuItem>}
                {data.map((user) => {
                    const userName = getUsersName(user);
                    const secondaryLabel = secondaryLabelField ? user[secondaryLabelField] : null;
                    const label = [userName, secondaryLabel].filter(Boolean).join(' - ');

                    return (
                        <MenuItem key={user._id} value={user._id}>
                            {label}
                            {highlightIcon && highlightUser === user._id && (
                                <Box
                                    component="span"
                                    sx={{
                                        ml: 1,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    {highlightIcon}
                                </Box>
                            )}
                        </MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
};

export default EditUserCell;
